import apiClient from './api-client';
import { LoanValuation } from './portfolio.service';

export interface ComparisonRequest {
  loan_ids: number[];
  scenarios?: string[];
}

export interface LoanComparisonData {
  loan_id: number;
  loan_name: string;
  scenarios: {
    base_case?: LoanValuation;
    stress_case?: LoanValuation;
    custom_scenario?: LoanValuation;
  };
}

export const comparisonService = {
  // Get loan valuation data for comparison
  async getLoansForComparison(
    loanIds: number[]
  ): Promise<LoanComparisonData[]> {
    try {
      // Get all loan valuations
      const valuations = await apiClient.get<LoanValuation[]>(
        '/api/v1/valuation/loan-valuation'
      );

      // Filter and group by loan
      const comparisonData: LoanComparisonData[] = loanIds.map((loanId) => {
        const loanValuations = valuations.data.filter(
          (v) => v.loan_id === loanId
        );

        return {
          loan_id: loanId,
          loan_name: loanValuations[0]?.loan_name || `Loan ${loanId}`,
          scenarios: {
            base_case: loanValuations.find(
              (v) => v.pricing_scenario === 'RPX Pricing'
            ),
            stress_case: loanValuations.find(
              (v) =>
                v.pricing_scenario === 'Custom Scenario' &&
                v.loan_status !== 'Default'
            ),
            custom_scenario: loanValuations.find(
              (v) =>
                v.pricing_scenario === 'Custom Scenario' &&
                v.loan_status === 'Default'
            ),
          },
        };
      });

      return comparisonData;
    } catch (error) {
      console.error('Error fetching comparison data:', error);
      throw error;
    }
  },

  // Get all available loans for selection
  async getAvailableLoans(): Promise<
    Array<{ id: number; name: string; balance: number; sector: string }>
  > {
    const response = await apiClient.get('/api/v1/loans/');
    const loans = Array.isArray(response.data)
      ? response.data
      : response.data.loans || [];

    return loans.map(
      (loan: {
        rp_system_id: number;
        client_loan_number: string;
        loan_name: string;
        current_balance: number;
        property_sector: string;
      }) => ({
        id: loan.rp_system_id,
        name: loan.loan_name,
        balance: loan.current_balance,
        sector: loan.property_sector,
      })
    );
  },
};
